import React from "react"

import { IconDotsVertical } from "@tabler/icons-react"
import { Cell, Row, flexRender } from "@tanstack/react-table"

import { cn } from "@/lib/utils"

import { Button } from "../../Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../DropdownMenu"
import { KEBAB_MENU_COLUMN_ID, DataTableProps } from "../DataTable"
import { TablePosition } from "../header/TableHead"
import { LE_TableInstance } from "../types"

const KEBAB_MENU_BUTTON_ID_SUFFIX = "kebab-menu-button"

export function TableBodyRow<TData extends Record<string, any> = object>({
  table,
  row,
  onRowClick,
  renderKebabMenuContent,
  setActiveMoreMenuRowId,
  active,
  moreMenuActive,
  classNameCell,
  style,
  position,
}: {
  table: LE_TableInstance<TData>
  row: Row<TData>
  onRowClick?: DataTableProps<TData>["onRowClick"]
  renderKebabMenuContent?: DataTableProps<TData>["renderKebabMenuContent"]
  setActiveMoreMenuRowId: React.Dispatch<React.SetStateAction<string | undefined>>
  active?: boolean
  moreMenuActive?: boolean
  classNameCell?: string | ((cell: Cell<TData, unknown>) => string)
  style?: React.CSSProperties
  position?: TablePosition
}) {
  const kebabMenuContent = renderKebabMenuContent?.(row.original)

  return (
    <tr
      key={row.id}
      className={cn(
        "group h-11 bg-white transition-colors hover:bg-colors-bg-primary-screen focus-visible:bg-colors-bg-primary-screen aria-selected:bg-brand-primary-100 [&:last-of-type>td]:border-b-0",
        onRowClick ? "cursor-pointer" : null,
      )}
      aria-selected={active || moreMenuActive || row.getIsSelected()}
      onClick={() => onRowClick?.(row)}
      tabIndex={onRowClick ? 0 : undefined}
      style={style}
    >
      {(position === "left"
        ? row.getLeftVisibleCells()
        : position === "center"
          ? row.getCenterVisibleCells()
          : position === "right"
            ? row.getRightVisibleCells()
            : row.getVisibleCells()
      ).map((cell) => {
        if (cell.column.id === KEBAB_MENU_COLUMN_ID) {
          return (
            <td
              className={cn(
                !position && "sticky -right-12 w-0",
                "h-full",
                "align-bottom text-black",
                "border-r border-b border-solid border-colors-border-secondary last-of-type:border-r-0",
                typeof classNameCell === "function"
                  ? classNameCell(cell)
                  : classNameCell,
              )}
              key={cell.id}
              style={{
                width: 42,
                minWidth: 42,
                maxWidth: 42,
              }}
            >
              {kebabMenuContent ? (
                <DropdownMenu
                  onOpenChange={(open) => {
                    setActiveMoreMenuRowId(open ? row.id : undefined)
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      id={table.getElementId(KEBAB_MENU_BUTTON_ID_SUFFIX)}
                      iconOnly
                      variant="neutral"
                      noBorder
                      className="my-1"
                      icon={IconDotsVertical}
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DropdownMenuTrigger>

                  <DropdownMenuContent onClick={(event) => event.stopPropagation()}>
                    {kebabMenuContent}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </td>
          )
        }

        const colParent = cell.column.parent
        const lastParentChild =
          colParent?.getLeafColumns().at(-1)?.id === cell.column.id

        return (
          <td
            className={cn(
              "h-full px-4 py-3 align-bottom text-sm text-black",
              "border-b border-solid border-colors-border-secondary last-of-type:border-r-0",
              cell.column.columnDef.meta?.cellClassName,
              {
                "sticky z-10 bg-white transition-colors group-hover:bg-colors-bg-primary-screen group-aria-selected:bg-brand-primary-100":
                  !position && cell.column.getIsPinned() === "left",
              },
              typeof classNameCell === "function" ? classNameCell(cell) : classNameCell,
              lastParentChild && "border-r",
            )}
            key={cell.id}
            style={{
              width: cell.column.columnDef.size ? cell.column.getSize() : undefined,
              ...(cell.column.columnDef.meta?.fixedSize
                ? {
                    minWidth: cell.column.getSize(),
                    maxWidth: cell.column.getSize(),
                  }
                : {}),
              ...(!position && cell.column.getIsPinned() == "left"
                ? {
                    left: `${cell.column.getStart("left")}px`,
                  }
                : {}),
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
    </tr>
  )
}

export const Memo_TableBodyRow = TableBodyRow as typeof TableBodyRow
